// How each lead PROVIDER is named on screen, and what it can do (step 3,
// 2026-10-06). Pure.
//
// ⚠️ `Record<LeadProvider, …>` throughout, so adding a provider to
// LEAD_PROVIDERS is a COMPILE ERROR here until its words exist — no surface can
// fall back to VirtualYard's vocabulary for a provider nobody wrote words for.
// Every screen that names the provider, its destination ("Store" / "Club") or
// the destination's id reads this table; none spells them out inline.

import type { LeadProvider } from '../../types/leadRelay.js';

export interface LeadProviderVocabulary {
  /** The system the lead is sent into, as a person names it. */
  system: string;
  /** What one route sends leads to: "Store" / "Club". */
  destination: string;
  /** The one field that picks it: "Store id (lid)" / "Club id". */
  destinationId: string;
  /** An example of that id, for placeholders. */
  destinationIdExample: string;
  /**
   * Whether its routes carry a form token. ⚠️ A route of a provider without one
   * reads "—" in the token column, never the red "none" that means a
   * VirtualYard route has LOST its token.
   */
  hasFormToken: boolean;
  /** Whether a lead to it carries a composed message (VirtualYard's Message field). */
  hasMessage: boolean;
}

export const PROVIDER_VOCABULARY: Record<LeadProvider, LeadProviderVocabulary> = {
  virtualyard: {
    system: 'VirtualYard',
    destination: 'Store',
    destinationId: 'Store id (lid)',
    destinationIdExample: '112:2820',
    hasFormToken: true,
    hasMessage: true,
  },
  anytimefitness: {
    system: 'Anytime Fitness',
    destination: 'Club',
    destinationId: 'Club id',
    destinationIdExample: 'AU-1432',
    hasFormToken: false,
    hasMessage: false,
  },
};

export function providerVocabulary(provider: LeadProvider): LeadProviderVocabulary {
  return PROVIDER_VOCABULARY[provider];
}

/**
 * The system a lead went (or goes) into, by name — or a neutral phrase when no
 * provider was ever resolved (an unassigned lead). ⚠️ Never VirtualYard by
 * default: naming a system the lead was never bound for is the bug this exists
 * to stop (an unassigned lead once read "Missing — VirtualYard requires this").
 */
export function systemName(provider: LeadProvider | null): string {
  return provider ? PROVIDER_VOCABULARY[provider].system : "the dealer's or club's system";
}

/**
 * A column heading over rows of possibly several providers: that provider's
 * noun when they all share one ("Store", "Club"), else "Destination".
 */
export function destinationHeading(providers: ReadonlyArray<LeadProvider | null>): string {
  const distinct = new Set(providers.filter((p): p is LeadProvider => p !== null));
  if (distinct.size !== 1) return 'Destination';
  return PROVIDER_VOCABULARY[[...distinct][0]!].destination;
}

/**
 * ⛔ WHETHER THE RELAY CAN SEND THROUGH THIS PROVIDER YET. Anytime Fitness
 * routes can be CREATED (step 3) but its transport is the next step, so until
 * this flips:
 *   · a route of a non-sending provider cannot be saved ACTIVE (routeRules),
 *     so the relay never routes a lead to it and Assign and send never offers it;
 *   · composeLead refuses it outright (`provider_not_wired`) — a Send test lead
 *     on one records a clear failure instead of reaching a transport that does
 *     not exist;
 *   · the edit page disables Send test lead and the Active tick, and says why.
 * The wiring step flips ONE value here.
 */
export const PROVIDER_CAN_SEND: Record<LeadProvider, boolean> = {
  virtualyard: true,
  // ✅ 2026-10-07: the AF transport exists (server/modules/leadRelay/
  // anytimefitnessAttempt.ts). An AF route can now be activated and sent a test
  // lead — and like every route it is CREATED inactive and in test mode
  // (routeForm.ts emptyCommon), so nothing reaches AF until a person turns it on.
  anytimefitness: true,
};

export function providerNotWiredMessage(provider: LeadProvider): string {
  return `Sending through ${PROVIDER_VOCABULARY[provider].system} is not built yet, so this route can be saved but not activated, and test leads cannot be sent.`;
}
